import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessWeek, HoursOfOperation } from '@models/hoursofoperation';
import { ServiceProvider } from '@models/service-provider';
import { ClinicsService } from '@services/clinics.service';
import { getBusinessWeek, getHoursOfOperationDays } from '@services/hoursofoperation.service';
import { ServiceProviderService } from '@services/service-provider.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

interface DayMinMaxHours {
  sun: { min: Date; max: Date };
  mon: { min: Date; max: Date };
  tue: { min: Date; max: Date };
  wed: { min: Date; max: Date };
  thu: { min: Date; max: Date };
  fri: { min: Date; max: Date };
  sat: { min: Date; max: Date };
}

@Component({
  selector: 'app-edit-online-booking-provider',
  templateUrl: './edit-online-booking-provider.component.html',
  styleUrls: ['./edit-online-booking-provider.component.less'],
})
export class EditOnlineBookingProviderComponent implements OnInit, OnDestroy {
  provider: ServiceProvider;
  loading = false;
  providerIdParam: string;
  form: FormGroup;
  private clinicHours: BusinessWeek;
  private unsub = new Subject<void>();

  readonly weekDays = {
    sun: 'Sunday',
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
  };

  dayMinMaxHours: DayMinMaxHours = {
    sun: { min: null, max: null },
    mon: { min: null, max: null },
    tue: { min: null, max: null },
    wed: { min: null, max: null },
    thu: { min: null, max: null },
    fri: { min: null, max: null },
    sat: { min: null, max: null },
  };

  private createDayFormGroup(): FormGroup {
    return new FormGroup({
      // day id is not used in the template but included here to make getting/setting onlineBookingHours easier.
      id: new FormControl(0),
      closed: new FormControl(true),
      openTime: new FormControl({ value: '', disabled: true }),
      closeTime: new FormControl({ value: '', disabled: true }),
    });
  }

  get useOnlineBookingHours(): boolean {
    return this.form.get('useOnlineBookingHours').value;
  }

  constructor(
    private serviceProviderService: ServiceProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private clinicsService: ClinicsService
  ) {
    this.form = new FormGroup({
      onlineBookingProviderName: new FormControl(''),
      useOnlineBookingHours: new FormControl(false),
      onlineBookingHours: new FormGroup({
        sun: this.createDayFormGroup(),
        mon: this.createDayFormGroup(),
        tue: this.createDayFormGroup(),
        wed: this.createDayFormGroup(),
        thu: this.createDayFormGroup(),
        fri: this.createDayFormGroup(),
        sat: this.createDayFormGroup(),
      }),
    });
  }

  ngOnInit() {
    this.loading = true;

    const onlineBookingHoursGroup = this.form.get('onlineBookingHours') as FormGroup;
    onlineBookingHoursGroup.disable();

    Object.keys(onlineBookingHoursGroup.controls).forEach((day) => {
      const dayGroup = onlineBookingHoursGroup.get(day) as FormGroup;
      dayGroup.get('closed').valueChanges.subscribe((checked: boolean) => {
        if (checked) {
          dayGroup.get('openTime').disable();
          dayGroup.get('closeTime').disable();
        } else if (this.useOnlineBookingHours) {
          dayGroup.get('openTime').enable();
          dayGroup.get('closeTime').enable();
        }
      });
    });

    this.form.get('useOnlineBookingHours').valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        const controls = (this.form.get('onlineBookingHours') as FormGroup).controls;
        Object.values(controls).forEach((control: FormGroup) => {
          const closedControl = control.get('closed');
          closedControl.enable();
          if (!closedControl.value) {
            control.get('openTime').enable();
            control.get('closeTime').enable();
          }
        });
      } else {
        this.form.get('onlineBookingHours').disable();
      }
    });

    this.clinicsService.clinicIdSelected$
      .pipe(
        takeUntil(this.unsub),
        filter((clinicId) => !!clinicId)
      )
      .subscribe(() => {
        this.clinicHours = getBusinessWeek(this.clinicsService.hoursOfOperation.hoursOfOperationDays);
        this.dayMinMaxHours = {
          sun: { min: this.clinicHours.sun.openTime, max: this.clinicHours.sun.closeTime },
          mon: { min: this.clinicHours.mon.openTime, max: this.clinicHours.mon.closeTime },
          tue: { min: this.clinicHours.tue.openTime, max: this.clinicHours.tue.closeTime },
          wed: { min: this.clinicHours.wed.openTime, max: this.clinicHours.wed.closeTime },
          thu: { min: this.clinicHours.thu.openTime, max: this.clinicHours.thu.closeTime },
          fri: { min: this.clinicHours.fri.openTime, max: this.clinicHours.fri.closeTime },
          sat: { min: this.clinicHours.sat.openTime, max: this.clinicHours.sat.closeTime },
        };
      });

    this.route.params.pipe(takeUntil(this.unsub)).subscribe((params) => {
      this.providerIdParam = params['provId'];
      if (this.providerIdParam !== '_' && this.providerIdParam != null) {
        this.loading = true;
        this.serviceProviderService.getServiceProviderById(this.providerIdParam).subscribe((provider) => {
          this.provider = provider;
          this.setForm();
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    });
  }

  private setForm() {
    this.form.controls['onlineBookingProviderName'].setValue(this.provider.onlineBookingProviderName);
    if (this.provider.onlineBookingHours === null) {
      const placeholder: HoursOfOperation = {
        hoursOfOperationId: 0,
        hoursOfOperationDays: this.clinicsService.hoursOfOperation.hoursOfOperationDays.slice(),
      };
      placeholder.hoursOfOperationDays.forEach((day) => {
        delete day.hoursOfOperationDayId;
      });
      this.provider.onlineBookingHours = placeholder;
    }
    this.form.controls['useOnlineBookingHours'].setValue(this.provider.useOnlineBookingHours);
    const week = getBusinessWeek(this.provider.onlineBookingHours.hoursOfOperationDays);
    this.form.controls['onlineBookingHours'].patchValue({
      // Hack to execute the weeks getter functions. patch not working otherwise
      sun: week.sun,
      mon: week.mon,
      tue: week.tue,
      wed: week.wed,
      thu: week.thu,
      fri: week.fri,
      sat: week.sat,
    });
  }

  updateProvider() {
    this.provider.onlineBookingProviderName = this.form.controls['onlineBookingProviderName'].value;
    this.provider.useOnlineBookingHours = this.form.controls['useOnlineBookingHours'].value;
    const onlineHours = getHoursOfOperationDays((this.form.controls['onlineBookingHours'] as FormGroup).getRawValue());
    if (this.provider.onlineBookingHours === null) {
      this.provider.onlineBookingHours = {
        hoursOfOperationId: 0,
        hoursOfOperationDays: onlineHours,
      };
    } else {
      this.provider.onlineBookingHours.hoursOfOperationDays = onlineHours;
    }
    this.serviceProviderService.updateServiceProviderBookingSettings(this.provider).subscribe(() => {
      this.serviceProviderService.updateRefreshRequired(true);
      this.router.navigate(['/management/organization/clinics/patient-portal', { outlets: { 'action-panel': null } }]);
    });
  }

  cancelUpdate() {
    this.serviceProviderService.updateRefreshRequired(false);
    this.router.navigate(['/management/organization/clinics/patient-portal', { outlets: { 'action-panel': null } }]);
  }

  originalOrder = () => 0;

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }
}
